import { PlusOutlined } from "@ant-design/icons";
import { Button, message, Table, Tag } from "antd";
import React from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import {
  Customer,
  Product,
  RedemptionsRequest,
  RowSelection,
} from "../helpers/shared-interfaces";

import * as Sentry from "@sentry/browser";
import { ColumnType } from "antd/lib/table/interface";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import { NavLink } from "react-router-dom";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import ExportData from "../components/ExportData";
import {
  fetchEnumOptions,
  formatSnakeCase,
  formatter,
  generateDisplayErrorMessage,
  generateNameFromCustomer,
  getStateColor,
  injectKey,
  request,
  responseHandler,
  showTotalPagination,
} from "../helpers/utilities";
import {
  GET_REDEMPTION_REQUEST_SORT_FIELDS,
  GET_REDEMPTION_REQUEST_STATE_ENUM,
  SEARCH_REDEMPTION_REQUEST,
} from "../gql/redemption-request";
import { PageToolbar } from "../components/PageToolbar";
import {
  buildQueryString,
  clearAllFilters,
  hasActiveFilters,
  parseQueryParams,
} from "../components/TableFilters";
import { DEBOUNCE_TIME } from "../helpers/constants";

interface RedemptionSearchQueryParams {
  page: number;
  q: string;
}

interface State {
  isLoading: boolean;
  redemptionsRequests: RedemptionsRequest[];
  redemptionRequestsCount: number;
  page: number;
  pageSize: number;
  q: string;
  state: string[];
  selectedIdsToExport: React.Key[];
  stateOptions: Array<{ value: string; label: string; color?: string }>;
  sortField: string;
  sortDirection: string;
  sortFieldOptions: Array<{ value: string; label: string }>;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
}

interface RedemptionsRequestAndProduct extends RedemptionsRequest {
  product?: Product;
}

export function getColumns<T extends RedemptionsRequest = RedemptionsRequest>(
  requests: RedemptionsRequest[],
): ColumnType<T>[] {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render(_: string, record: RedemptionsRequest): JSX.Element {
        return <span>{record.id}</span>;
      },
    },
    {
      title: "Batch Number",
      dataIndex: "batchNumber",
      key: "batchNumber",
      render(_: string, record: RedemptionsRequest): JSX.Element {
        return <span>{record.batchNumber}</span>;
      },
    },
    {
      title: "Request Date",
      dataIndex: "requestDate",
      key: "requestDate",
      render(_: string, record: RedemptionsRequest): JSX.Element {
        return (
          <span>
            {record.requestDate
              ? moment(record.requestDate, "YYYY-MM-DD HH:mm:ss").format("llll")
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Customer",
      dataIndex: ["customer", "firstName"],
      key: "customer",
      render(_: string, record: RedemptionsRequest): JSX.Element {
        if (record.customer) {
          return (
            <NavLink
              to={{
                pathname: `/redemption-requests/${record.id}`,
                state: { type: "redemption", requests },
              }}
            >
              {generateNameFromCustomer(record.customer as Customer)}
            </NavLink>
          );
        } else {
          return (
            <NavLink
              to={{
                pathname: `/redemption-requests/${record.id}`,
                state: { type: "redemption", requests },
              }}
            >
              {record.company?.name}
            </NavLink>
          );
        }
      },
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render(_: string, record: RedemptionsRequest): JSX.Element {
        return (
          <NavLink
            to={{
              pathname: `/companies/${record.company?.id}`,
            }}
          >
            {record.company?.name}
          </NavLink>
        );
      },
    },
    {
      title: "Product",
      dataIndex: ["customerSubscription,product,name"],
      key: "productName",
      render(text: string, record: RedemptionsRequestAndProduct): JSX.Element {
        return (
          <span>
            {`${
              record.customerSubscription?.product?.name ??
              record?.product?.name
            }`}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "state",
      key: "state",
      render(state: string): JSX.Element {
        return (
          <Tag
            color={getStateColor(state)}
            style={{ textTransform: "capitalize" }}
          >
            {formatSnakeCase(state)}
          </Tag>
        );
      },
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render(_: string, record: RedemptionsRequest): JSX.Element {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {formatSnakeCase(record.source)}
          </span>
        );
      },
    },
    {
      title: "Redemption Amount/Percentage",
      dataIndex: "redemptionValue",
      key: "redemptionValue",
      render(_: string, record: RedemptionsRequest): JSX.Element {
        if (record.redemptionType === "amount") {
          return <span>{formatter.format(record.redemptionValue)}</span>;
        }
        return <span>{`${record.redemptionValue}%`}</span>;
      },
    },
  ];
}

export default class Redemptions extends React.Component<
  RouteComponentProps,
  State
> {
  private _isMounted = false;

  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      redemptionsRequests: [],
      redemptionRequestsCount: 0,
      page: 1,
      pageSize: 25,
      q: "",
      state: [],
      selectedIdsToExport: [],
      stateOptions: [],
      sortField: "CREATED_AT",
      sortDirection: "DESC",
      sortFieldOptions: [],
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
    };
    this.redirectToNewRedemptionsRequestPage =
      this.redirectToNewRedemptionsRequestPage.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
  }

  toggleAdvancedSearchModel = (): void => {
    this.setState((prevState) => ({
      showAdvancedSearchModal: !prevState.showAdvancedSearchModal,
    }));
  };

  handleOnAdvancedSearchError = (error: string): void => {
    message.error(error);
    this.setState({ isLoading: true });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ): void => {
    this.setState({
      isLoading: false,
      redemptionsRequests: data.results as RedemptionsRequest[],
      redemptionRequestsCount: data.count,
      selectedFieldsToDisplay: fields,
      removedField: undefined,
    });
  };

  handleOnFieldRemove = (field: IField): void => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState(
      { removedField: field, selectedFieldsToDisplay: fields },
      () => {
        if (fields.length === 0) this.triggerImmediateSearch();
      },
    );
  };

  triggerImmediateSearch = async (): Promise<void> => {
    const { q, page, pageSize, state, sortField, sortDirection } = this.state;
    interface Response {
      count: number;
      results: RedemptionsRequest[];
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: SEARCH_REDEMPTION_REQUEST,
        variables: {
          q,
          page,
          numberPerPage: pageSize,
          states: state,
          orderBy: { field: sortField, direction: sortDirection },
        },
      });

      const { handled, mutation } = responseHandler<Response>(
        response,
        "redemptionRequestSearch",
      );

      if (handled) {
        message.error("Failed To Load Redemption Requests");
      } else {
        this.syncBrowserQueryString();
        this.setState({
          redemptionsRequests: mutation.results,
          redemptionRequestsCount: mutation.count,
        });
      }
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  syncBrowserQueryString = (): void => {
    const queryStringResult = buildQueryString(
      {
        ...this.state,
        selectedStates: this.state.state,
      } as any,
      {
        includeSort: true,
      },
    );
    this.props.history.replace(queryStringResult);
  };

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState({ isLoading: true, page, pageSize: pageSize }, async () => {
      if (this.state.selectedFieldsToDisplay.length < 1) {
        await this.triggerImmediateSearch();
      }
      this.setState({ isLoading: false });
    });
  }

  fetchStateEnums = async (): Promise<void> => {
    const stateOptions = await fetchEnumOptions(
      GET_REDEMPTION_REQUEST_STATE_ENUM,
      {
        includeColor: true,
        errorMsg: "Failed to fetch state enums",
      },
    );

    if (this._isMounted) {
      this.setState({ stateOptions });
    }
  };

  fetchSortFieldOptions = async (): Promise<void> => {
    const sortFieldOptions = await fetchEnumOptions(
      GET_REDEMPTION_REQUEST_SORT_FIELDS,
      {
        errorMsg: "Failed to fetch sort options",
      },
    );

    if (this._isMounted) {
      this.setState({ sortFieldOptions });
    }
  };

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value });
    await this.triggerDelayedSearch();
  };

  mergeWithLocalState = (callback?: () => void): void => {
    const parsed = parseQueryParams({
      includeSort: true,
    });

    this.setState(
      {
        q: parsed.q || "",
        page: parsed.page || 1,
        state: parsed.selectedStates || [],
        sortField: parsed.sortField || "CREATED_AT",
        sortDirection: parsed.sortDirection || "DESC",
      },
      callback,
    );
  };

  handleStateFilter = (states: string[]) => {
    this.setState({ state: states, page: 1 }, () => {
      this.triggerImmediateSearch();
    });
  };

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    await Promise.all([this.fetchStateEnums(), this.fetchSortFieldOptions()]);

    this.mergeWithLocalState(() => {
      this.triggerImmediateSearch();
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  redirectToNewRedemptionsRequestPage = (): void => {
    this.props.history.push("/redemption-requests/new");
  };

  onSelectChange(newSelectedIdsToExport: React.Key[]) {
    this.setState({
      selectedIdsToExport: Array.from(newSelectedIdsToExport).map(
        (item) => item,
      ),
    });
  }

  handleSortChange = (field: string, direction: string): void => {
    this.setState(
      { sortField: field, sortDirection: direction, page: 1 },
      () => {
        this.triggerImmediateSearch();
      },
    );
  };

  render(): JSX.Element | null {
    const {
      redemptionsRequests,
      redemptionRequestsCount,
      page,
      q,
      selectedIdsToExport,
      pageSize,
      sortField,
      sortDirection,
      showAdvancedSearchModal,
      selectedFieldsToDisplay,
      removedField,
    } = this.state;

    const rowSelection: RowSelection = {
      selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };

    const isFiltering = hasActiveFilters(
      {
        q,
        page,
        selectedStates: this.state.state,
        sortField,
        sortDirection,
      },
      {
        includeSort: true,
      },
    );

    return (
      <React.Fragment>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={showAdvancedSearchModal}
          model="redemptionRequest"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          pageSize={pageSize}
          page={page}
        />
        <Card>
          <PageToolbar
            advancedSearch={{
              onOpen: this.toggleAdvancedSearchModel,
              activeFields: selectedFieldsToDisplay,
              onFieldRemove: this.handleOnFieldRemove,
            }}
            onSearchChange={this.handleSearchInput}
            searchValue={q}
            searchPlaceholder="Search by customer, company, or external reference"
            actions={
              <ExportData
                model="redemptionRequest"
                dataArray={redemptionsRequests}
                size="large"
                customIds={selectedIdsToExport}
              />
            }
            primaryAction={
              <EntitledSection
                entitlement={entitlements.RedemptionRequests.create}
              >
                <Button
                  className="base-button"
                  size="large"
                  onClick={this.redirectToNewRedemptionsRequestPage}
                >
                  <PlusOutlined /> Create Redemption Request
                </Button>
              </EntitledSection>
            }
            totalCount={redemptionRequestsCount}
            currentDataCount={redemptionsRequests.length}
            page={page}
            pageSize={pageSize}
            selectedCount={selectedIdsToExport.length}
            filters={
              <PageToolbar.Filters
                isFiltering={isFiltering}
                onClear={() => clearAllFilters(this)}
                configs={[
                  {
                    label: "Status",
                    options: this.state.stateOptions,
                    selectedValues: this.state.state,
                    onChange: this.handleStateFilter,
                    width: 180,
                  },
                ]}
                sort={{
                  field: sortField,
                  direction: sortDirection,
                  options: this.state.sortFieldOptions,
                  onChange: this.handleSortChange,
                }}
              />
            }
            modelName="redemption requests"
          />

          <div className="pt-table-container">
            <Table
              scroll={{ x: 1000 }}
              columns={getColumns(redemptionsRequests)}
              dataSource={injectKey<RedemptionsRequest>(
                redemptionsRequests,
                "id",
              )}
              loading={this.state.isLoading}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: redemptionRequestsCount,
                onChange: (p, s) => this.nextPage(p, s),
                showTotal: showTotalPagination,
              }}
              rowSelection={rowSelection}
            />
          </div>
        </Card>
      </React.Fragment>
    );
  }
}
